import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, List, ListItem, ListIcon, Button, Badge } from "@chakra-ui/react";
import { FaCheckCircle, FaHome, FaSun, FaDollarSign, FaLeaf, FaChartLine, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const SolutionsFirstHomeSolar: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/first-home-solar"
        fallbackTitle="First Home + Solar Solutions"
        fallbackDescription="Affordable home ownership with built-in solar benefits"
      />
      <NavbarWithCallToAction />

      {/* Hero Section with Badge */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg="#C2A878" color={brandConfig.colors.primary} fontSize="md" px={4} py={2} borderRadius="full">
              First Home Buyer Special
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              First Home + Solar Package
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Start your homeownership journey with solar already installed
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Two-Column Comparison: Without Solar vs With Solar */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          The Solar Advantage for First Home Buyers
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box bg="gray.100" p={8} borderRadius="lg" boxShadow="sm">
            <VStack align="start" spacing={4}>
              <Heading size="md" color="gray.700">Without Solar</Heading>
              <Box w="100%" h="2px" bg="gray.300" />
              <VStack align="start" spacing={3} w="100%">
                <HStack>
                  <Text fontWeight="bold">Mortgage:</Text>
                  <Text>$2,500/month</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">Electricity:</Text>
                  <Text>$350/month</Text>
                </HStack>
                <Box w="100%" h="1px" bg="gray.300" />
                <HStack>
                  <Text fontWeight="bold" fontSize="lg">Total:</Text>
                  <Text fontSize="lg" color="gray.800" fontWeight="bold">$2,850/month</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Rising electricity costs reduce buying power over time
                </Text>
              </VStack>
            </VStack>
          </Box>

          <Box bg="white" p={8} borderRadius="lg" boxShadow="lg" border="2px solid" borderColor="#C2A878">
            <VStack align="start" spacing={4}>
              <HStack>
                <Heading size="md" color={brandConfig.colors.primary}>With Solar Package</Heading>
                <Badge bg="#C2A878" color={brandConfig.colors.primary}>Save $200/mo</Badge>
              </HStack>
              <Box w="100%" h="2px" bg="#C2A878" opacity={0.3} />
              <VStack align="start" spacing={3} w="100%">
                <HStack>
                  <Text fontWeight="bold">Mortgage + Solar:</Text>
                  <Text>$2,650/month</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold">Electricity:</Text>
                  <Text>$0/month</Text>
                  <Badge bg="#C2A878" color={brandConfig.colors.primary} fontSize="xs">Covered</Badge>
                </HStack>
                <Box w="100%" h="1px" bg="#C2A878" opacity={0.3} />
                <HStack>
                  <Text fontWeight="bold" fontSize="lg">Total:</Text>
                  <Text fontSize="lg" color={brandConfig.colors.primary} fontWeight="bold">$2,650/month</Text>
                </HStack>
                <Text fontSize="sm" color={brandConfig.colors.primary} mt={2}>
                  Fixed costs + increased property value from day one
                </Text>
              </VStack>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Benefits Grid (6 benefits) */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            6 Reasons First Home Buyers Choose Solar
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaDollarSign} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Lower Monthly Costs</Heading>
              <Text fontSize="sm" color="gray.600">
                Save $150-$300 per month on electricity, making homeownership more affordable
              </Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaHome} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Increased Property Value</Heading>
              <Text fontSize="sm" color="gray.600">
                Solar adds $15,000-$25,000 to your home's value immediately
              </Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaShieldAlt} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Price Protection</Heading>
              <Text fontSize="sm" color="gray.600">
                Lock in energy costs while others face rising electricity prices
              </Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaSun} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Government Incentives</Heading>
              <Text fontSize="sm" color="gray.600">
                Access first home buyer grants plus solar rebates and credits
              </Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaLeaf} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Environmental Impact</Heading>
              <Text fontSize="sm" color="gray.600">
                Reduce your carbon footprint and contribute to a sustainable future
              </Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Box as={FaChartLine} size="32px" color={brandConfig.colors.primary} mb={3} />
              <Heading size="sm" mb={2}>Future-Proof Investment</Heading>
              <Text fontSize="sm" color="gray.600">
                Modern homes need solar - stay ahead of building regulations
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Government Incentives Section */}
      <Container maxW="container.xl" py={16}>
        <Box bg="gray.50" p={10} borderRadius="xl" border="2px solid" borderColor="#C2A878">
          <Heading size="lg" mb={6} textAlign="center" color={brandConfig.colors.primary}>
            First Home Buyer Incentives You Can Stack
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack align="start" spacing={3}>
              <HStack>
                <Box as={FaCheckCircle} color={brandConfig.colors.primary} />
                <Text fontWeight="bold">First Home Owner Grant</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" pl={6}>
                Up to $10,000 for eligible first home buyers
              </Text>
            </VStack>

            <VStack align="start" spacing={3}>
              <HStack>
                <Box as={FaCheckCircle} color={brandConfig.colors.primary} />
                <Text fontWeight="bold">Solar Rebate Programs</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" pl={6}>
                State-specific rebates up to $1,400 for solar installation
              </Text>
            </VStack>

            <VStack align="start" spacing={3}>
              <HStack>
                <Box as={FaCheckCircle} color={brandConfig.colors.primary} />
                <Text fontWeight="bold">Small-scale Technology Certificates</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" pl={6}>
                Federal incentive worth $2,500-$3,500 depending on system size
              </Text>
            </VStack>

            <VStack align="start" spacing={3}>
              <HStack>
                <Box as={FaCheckCircle} color={brandConfig.colors.primary} />
                <Text fontWeight="bold">Interest-Free Solar Loans</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" pl={6}>
                Some states offer 0% interest loans for solar installations
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>
      </Container>

      {/* Getting Started Checklist (5 steps) */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            Your 5-Step Journey to Solar Homeownership
          </Heading>
          <VStack spacing={4} align="stretch" maxW="2xl" mx="auto">
            <HStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4}>
              <Box bg={brandConfig.colors.primary} color="white" minW="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                1
              </Box>
              <VStack align="start" flex="1" spacing={1}>
                <Text fontWeight="bold">Pre-Approval Assessment</Text>
                <Text fontSize="sm" color="gray.600">
                  Get pre-approved for your home loan and solar financing together
                </Text>
              </VStack>
            </HStack>

            <HStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4}>
              <Box bg={brandConfig.colors.primary} color="white" minW="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                2
              </Box>
              <VStack align="start" flex="1" spacing={1}>
                <Text fontWeight="bold">Find Your Home</Text>
                <Text fontSize="sm" color="gray.600">
                  Search for properties while we assess solar potential of each option
                </Text>
              </VStack>
            </HStack>

            <HStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4}>
              <Box bg={brandConfig.colors.primary} color="white" minW="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                3
              </Box>
              <VStack align="start" flex="1" spacing={1}>
                <Text fontWeight="bold">Solar System Design</Text>
                <Text fontSize="sm" color="gray.600">
                  Custom solar solution designed for your specific property
                </Text>
              </VStack>
            </HStack>

            <HStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4}>
              <Box bg={brandConfig.colors.primary} color="white" minW="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                4
              </Box>
              <VStack align="start" flex="1" spacing={1}>
                <Text fontWeight="bold">Combine Financing</Text>
                <Text fontSize="sm" color="gray.600">
                  Roll solar costs into your mortgage or set up a separate low-rate loan
                </Text>
              </VStack>
            </HStack>

            <HStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4}>
              <Box bg={brandConfig.colors.primary} color="white" minW="40px" h="40px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                5
              </Box>
              <VStack align="start" flex="1" spacing={1}>
                <Text fontWeight="bold">Move In & Save</Text>
                <Text fontSize="sm" color="gray.600">
                  Solar installed before or shortly after settlement - start saving immediately
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={6} textAlign="center">
          <Heading size="xl">Ready for Your First Home with Solar?</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Book a free consultation to learn how we can help you buy your first home with solar included, often for less than renting.
          </Text>
          <HStack spacing={4}>
            <Button size="lg" bg="#C2A878" color={brandConfig.colors.primary} _hover={{ bg: "#b09060" }} fontWeight="bold" onClick={() => navigate("/contact")}>
              Book Free Consultation
            </Button>
            <Button size="lg" variant="outline" borderColor={brandConfig.colors.primary} color={brandConfig.colors.primary} onClick={() => navigate("/contact")}>
              Calculate My Savings
            </Button>
          </HStack>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsFirstHomeSolar;
